import {
  IconBell,
  IconConfetti,
  IconFileText,
  IconHammer,
  IconKey,
  IconNote,
  IconTool,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import type { CalendarEvent } from 'src/Components/Calendars/MonthCalendar/MonthCalendar.types';
import { EventDtoTypeEnum } from 'src/Services/API/generated';
import {
  FALLBACK_HOUSEHOLD_COLOR,
  getEventVisual,
  type EventVisualIcon,
} from 'src/Utils/eventVisuals';

import styles from '../ListCalendar.module.scss';

const CHIP_ICON_MAP: Record<EventVisualIcon, typeof IconNote> = {
  IconTool,
  IconHammer,
  IconKey,
  IconFileText,
  IconConfetti,
  IconNote,
  IconBell,
};

interface Props {
  event: CalendarEvent;
  onClick: (event: CalendarEvent) => void;
}

const EventCard = ({ event, onClick }: Props) => {
  const { t, i18n } = useTranslation();
  const visual = getEventVisual(event);
  const isRibbon = visual.kind === 'ribbon';
  const localizedStart = event.startTime.locale(i18n.language);
  const localizedEnd = event.endTime?.locale(i18n.language);

  const buildSubtitle = (): string => {
    if (isRibbon) {
      if (!localizedEnd) return '';
      // R7 — a stay is measured in nights, not in calendar days.
      const nights = event.endTime!.startOf('day').diff(event.startTime.startOf('day'), 'day');
      const range = t('planning.listView.fromTo', {
        defaultValue: 'Du {{start}} au {{end}}',
        start: localizedStart.format('ddd D'),
        end: localizedEnd.format('ddd D MMM'),
      });
      const duration =
        nights === 0
          ? t('planning.listView.oneDay', { defaultValue: '1 journée' })
          : t('planning.listView.nights', { defaultValue: '{{count}} nuits', count: nights });
      return `${range} · ${duration}`;
    }

    if (event.allDay) return t('planning.listView.allDay', { defaultValue: 'Toute la journée' });

    if (localizedEnd && !event.startTime.isSame(event.endTime, 'day')) {
      return t('planning.listView.fromTo', {
        defaultValue: 'Du {{start}} au {{end}}',
        start: localizedStart.format('D'),
        end: localizedEnd.format('D MMM'),
      });
    }

    return localizedStart.format('LT');
  };

  const typeLabel = isRibbon
    ? event.type === EventDtoTypeEnum.Location
      ? t('createEventForm.eventTypes.LOCATION', { defaultValue: 'Location' })
      : t('planning.listView.stay', { defaultValue: 'Séjour' })
    : t(`createEventForm.eventTypes.${event.type}`, { defaultValue: event.type });

  const Icon = visual.icon ? CHIP_ICON_MAP[visual.icon] : IconNote;
  const subtitle = buildSubtitle();

  return (
    <button className={styles.eventCard} onClick={() => onClick(event)} type="button">
      {isRibbon ? (
        <span
          className={styles.eventBar}
          style={{ backgroundColor: visual.color ?? FALLBACK_HOUSEHOLD_COLOR }}
        />
      ) : (
        <Icon size={20} stroke={1.75} className={styles.chipIcon} />
      )}

      <span className={styles.eventText}>
        <span className={styles.eventLabel}>{visual.label}</span>
        {subtitle && <span className={styles.eventSub}>{subtitle}</span>}
      </span>

      <span
        className={styles.typeChip}
        style={
          isRibbon
            ? { backgroundColor: visual.color ?? FALLBACK_HOUSEHOLD_COLOR, color: '#ffffff' }
            : undefined
        }
      >
        {typeLabel}
      </span>
    </button>
  );
};

export default EventCard;
